import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { isiComponentForKey } from '../../isi/registry';
import { evaluateAnswer, userAttributesFromUser } from '../../../helpers/questionnaire_rules';
import { questionsFromQuestionnaire, progressFor } from '../../../helpers/questionnaire_definition';
import { findAndSetMentor } from '../../../helpers/find_mentor_helper';
import {
  getDueQuestionnaireResponses,
  getQuestionnaireResponse,
  answerQuestionnaireResponse,
  createDocusealSubmission,
  getDocusealSigningState,
  redoDocusealConsent,
  searchSites,
} from '../../../api/v2/questionnaire_responses';
import { getConversationSurveyResponse, answerConversationSurveyResponse } from '../../../api/conversation_surveys';

import QuestionRenderer from './question_renderer';
import QuestionnaireStopperV2 from '../question_components/questionnaire_stopper_v2';
import ImportantSafetyInformationStickyFooterV2 from '../../isi/sticky_footer_v2';
import Context from '../../../utils/context';
import QuestionnaireLocaleContext from '../locale_context';
import LanguageSwitcher from '../language_switcher';
import { chromeString, detectLocale, translateKnownMessage } from '../../../helpers/questionnaire_translations';

const OTHER_SUFFIX_SEPARATOR = ': ';

const currentQuestionFor = (response, questions) => {
  if (!response || response.completed) return null;
  if (response.current_question_id) {
    return questions.find((question) => question.id === response.current_question_id) || null;
  }
  return questions[0] || null;
};

const answerWithOther = (question, answer, otherValue) => {
  if (!question.allowOther || !otherValue || otherValue.trim() === '') return answer;

  const other = `Other${OTHER_SUFFIX_SEPARATOR}${otherValue.trim()}`;
  if (question.componentType === 'checkbox_pill') {
    return [...(Array.isArray(answer) ? answer : []), other];
  }
  return answer || other;
};

/**
 * Member-facing flow for admin-built questionnaires and post conversation
 * surveys. With a :uuid param it runs that single response; without one it
 * works through every response due at login, one after the other.
 *
 * The post conversation survey page mounts this directly with
 * conversationSurveyUuid and onFinish. An end rule is the done state: Home
 * calls onFinish, which returns the member to their conversations. In that
 * mode the response is read and answered through the unauthenticated
 * conversation-survey endpoints (keyed by the survey uuid) so the survey
 * keeps working while logged out.
 */
const DynamicQuestionnaire = ({ responseUuid, conversationSurveyUuid, onFinish }) => {
  const history = useHistory();
  const params = useParams();
  const uuid = responseUuid || params.uuid;
  const context = useContext(Context);

  const [queue, setQueue] = useState(null);
  const [response, setResponse] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [otherValue, setOtherValue] = useState('');
  const [stopper, setStopper] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Answered question ids for the response in progress, in order, so Back can
  // step through them. viewingQuestionId overrides the server's
  // current_question_id while the member is looking at a question they've
  // already passed; it clears once they submit forward again.
  const [answeredQuestionIds, setAnsweredQuestionIds] = useState([]);
  const [viewingQuestionId, setViewingQuestionId] = useState(null);
  const [locale, setLocale] = useState('en');
  const isiRef = useRef(null);

  const finish = () => {
    if (onFinish) {
      onFinish();
      return;
    }

    const user = context.auth.getCurrentUser();
    if (user.matched_with_mentor) {
      history.push('/member/conversations');
    } else {
      context.auth.userUpdate({ matched_with_mentor: true }, () => {
        history.push('/member/conversations');
      });
    }
  };

  const beginResponse = (nextResponse, remaining) => {
    setQueue(remaining);
    setResponse(nextResponse);
    setAnswer(null);
    setOtherValue('');
    setStopper(null);
    setAnsweredQuestionIds([]);
    setViewingQuestionId(null);

    const enabledLocales = (nextResponse.questionnaire && nextResponse.questionnaire.locales) || [];
    const preferred = (typeof navigator !== 'undefined' && (navigator.languages || [navigator.language])) || [];
    setLocale(enabledLocales.length > 0 ? detectLocale(preferred, enabledLocales) : 'en');
  };

  const advanceQueue = (remaining) => {
    if (!remaining || remaining.length === 0) {
      finish();
      return;
    }
    beginResponse(remaining[0], remaining.slice(1));
  };

  const loadResponses = () => {
    if (conversationSurveyUuid) {
      return getConversationSurveyResponse(conversationSurveyUuid).then((record) => [record]);
    }
    if (uuid) {
      return getQuestionnaireResponse(uuid).then((record) => [record]);
    }
    return getDueQuestionnaireResponses().then((payload) => payload.questionnaire_responses || []);
  };

  useEffect(() => {
    loadResponses()
      .then((records) => {
        const pending = records.filter((record) => !record.completed);
        if (pending.length === 0) {
          finish();
          return;
        }
        beginResponse(pending[0], pending.slice(1));
      })
      .catch((loadError) => setError(loadError.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, conversationSurveyUuid]);

  // A response with no askable question (e.g. an empty questionnaire) moves
  // straight to the next one in the queue.
  useEffect(() => {
    if (!response || stopper) return;

    const questions = questionsFromQuestionnaire(response.questionnaire);
    if (!currentQuestionFor(response, questions)) advanceQueue(queue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  const activeTranslations = (response && response.questionnaire && response.questionnaire.translations) || {};
  const enabledLocales = (response && response.questionnaire && response.questionnaire.locales) || [];
  const chrome = (key) => chromeString(activeTranslations, locale, key);

  if (error) {
    return (
      <div style={{ padding: '80px 24px', textAlign: 'center' }}>
        <p>
          {chrome('load_error')} {error}
        </p>
      </div>
    );
  }

  if (stopper) {
    return (
      <QuestionnaireStopperV2
        heading={stopper.heading}
        body={stopper.body}
        onContinue={stopper.completion ? () => advanceQueue(queue) : undefined}
        continueLabel={queue && queue.length > 0 ? chrome('continue') : chrome('home')}
      />
    );
  }

  if (!response) return null;

  const questions = questionsFromQuestionnaire(response.questionnaire);
  const liveQuestion = currentQuestionFor(response, questions);
  const question = viewingQuestionId
    ? questions.find((candidate) => candidate.id === viewingQuestionId) || liveQuestion
    : liveQuestion;

  if (!question) return null;

  const { current: questionPosition, total: questionTotal } = progressFor(question, questions);
  const storedAnswer = answer === null ? response.answers[question.dataKey] : answer;

  // Answering routes through the unauthenticated conversation-survey endpoint
  // (keyed by the survey uuid) in survey mode, and the authenticated response
  // endpoint (keyed by the response uuid) otherwise.
  const answerResponse = (questionId, value) =>
    conversationSurveyUuid
      ? answerConversationSurveyResponse(conversationSurveyUuid, questionId, value)
      : answerQuestionnaireResponse(response.uuid, questionId, value);

  const submitValue = (value) => {
    if (saving) return;

    setSaving(true);
    setError(null);

    answerResponse(question.id, value)
      .then((updated) => {
        setSaving(false);

        // Mirrors the server's rule-matching context: previously recorded
        // answers layered over the member's mentor match data, plus the answer
        // just given, so answer_data_key rules resolve the same way here.
        const ruleAnswers = {
          ...(response.match_data || {}),
          ...response.answers,
          [question.dataKey]: value,
        };

        if (updated.disqualified) {
          const userAttributes = userAttributesFromUser(context.auth.getCurrentUser());
          const result = evaluateAnswer(questions, question.ref, value, userAttributes, ruleAnswers);
          setStopper({
            heading: (result.rule && result.rule.stopperHeading) || chrome('default_end_heading'),
            body: (result.rule && result.rule.stopperBody) || '',
          });
          return;
        }

        if (updated.completed) {
          // An end rule with a heading or body shows that screen. Home then
          // leaves. A rule saved with neither still completes and leaves for
          // conversations without a screen.
          const userAttributes = userAttributesFromUser(context.auth.getCurrentUser());
          const result = evaluateAnswer(questions, question.ref, value, userAttributes, ruleAnswers);
          if (result.rule && (result.rule.stopperHeading || result.rule.stopperBody)) {
            setStopper({
              heading: result.rule.stopperHeading || chrome('default_completion_heading'),
              body: result.rule.stopperBody || '',
              completion: true,
            });
            return;
          }

          advanceQueue(queue);
          return;
        }

        setAnsweredQuestionIds((previous) => [...previous, question.id]);
        setViewingQuestionId(null);
        setResponse(updated);
        setAnswer(null);
        setOtherValue('');
      })
      .catch((submitError) => {
        setSaving(false);
        setError(translateKnownMessage(question, submitError.message, activeTranslations, locale));
      });
  };

  const submit = () => {
    submitValue(answerWithOther(question, storedAnswer === undefined ? '' : storedAnswer, otherValue));
  };

  const onRequestMentorMatch = (callbacks) => findAndSetMentor(callbacks, context);

  // A mentor_match stage has no answer to record. MentorMatchV2 owns the
  // rest of the flow from here — its own Continue button hands the member
  // straight off to the new conversation, or (with no mentor found) it shows
  // a dead-end message — so this only advances the response's bookkeeping in
  // the background.
  const onMentorMatchOutcome = () => {
    answerResponse(question.id, null).catch(() => {});
  };

  // Steps back to the previously answered question, showing its saved
  // answer. A no-op on the first question of a response, same as the legacy
  // per-step flows.
  const goBack = () => {
    if (answeredQuestionIds.length === 0) return;

    setViewingQuestionId(answeredQuestionIds[answeredQuestionIds.length - 1]);
    setAnsweredQuestionIds((previous) => previous.slice(0, -1));
    setAnswer(null);
    setOtherValue('');
  };

  const IsiComponent = isiComponentForKey(question.isiKey);

  const localeNames = (response.questionnaire && response.questionnaire.locale_names) || {};
  const switcherOptions = [
    { code: 'en', name: 'English' },
    ...enabledLocales.map((code) => ({ code, name: localeNames[code] || code })),
  ];
  const switcher =
    enabledLocales.length > 0 ? (
      <LanguageSwitcher options={switcherOptions} value={locale} onChange={setLocale} />
    ) : null;
  const localeValue = { locale, translations: activeTranslations, chrome, switcher };

  return (
    <QuestionnaireLocaleContext.Provider value={localeValue}>
      <QuestionRenderer
        question={question}
        current={questionPosition}
        total={questionTotal}
        answer={storedAnswer}
        onAnswerChange={setAnswer}
        onSubmit={submit}
        onBack={answeredQuestionIds.length > 0 ? goBack : undefined}
        otherValue={otherValue}
        onOtherChange={setOtherValue}
        onRequestDocusealSubmission={() => createDocusealSubmission(response.uuid, question.id)}
        onRequestDocusealSigningState={() => getDocusealSigningState(response.uuid, question.id)}
        onRedoDocusealConsent={() => redoDocusealConsent(response.uuid, question.id)}
        onDocusealComplete={(detail) => submitValue(String(detail.submission_id || detail.id || 'signed'))}
        onRequestMentorMatch={onRequestMentorMatch}
        onMentorMatchOutcome={onMentorMatchOutcome}
        onSearchSites={(query) => searchSites(response.uuid, query)}
        firstMessageAnswer={question.firstMessageDataKey ? response.answers[question.firstMessageDataKey] : undefined}
      />
      {IsiComponent && (
        <ImportantSafetyInformationStickyFooterV2
          ref={isiRef}
          initialHeight={300}
          initialHeightMobile={200}
          expandedHeight={800}
          component={<IsiComponent />}
        />
      )}
    </QuestionnaireLocaleContext.Provider>
  );
};

export default DynamicQuestionnaire;
